/**
 * Device-local cache for the per-user data key, used by private (E2EE) mode.
 *
 * In private mode the server cannot unwrap the data key, so once a device has
 * obtained the key (at setup, or by entering the recovery key) we cache it here
 * so subsequent same-device sessions are seamless. The CryptoKey is stored
 * directly in IndexedDB (structured-clone supports CryptoKey), as a
 * non-extractable key where the browser allows it.
 *
 * Standard (Tier 2) mode does not use this — the server unwraps each session.
 */

const DB_NAME = 'jilu-keystore';
const STORE = 'keys';
const DB_VERSION = 1;

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

/** Per-user key so multiple accounts on one device don't collide. */
const keyId = (userId: string) => `dataKey:${userId}`;

export const cacheDataKey = async (
  userId: string,
  key: CryptoKey
): Promise<void> => {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(key, keyId(userId));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
};

export const getCachedDataKey = async (
  userId: string
): Promise<CryptoKey | null> => {
  const db = await openDb();
  try {
    return await new Promise<CryptoKey | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(keyId(userId));
      req.onsuccess = () => resolve((req.result as CryptoKey) ?? null);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
};

export const clearCachedDataKey = async (userId: string): Promise<void> => {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(keyId(userId));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
};
